<template>
  <div>
    <LinkookLiveBanner :class="$style.linkookLiveBanner" v-if="!loading" />
    <div :class="$style.container">
      <!-- 로딩 상태 -->
      <LoadingScreen v-if="loading" loading-text="커미션을 불러오는 중..." />

      <!-- 에러 상태 -->
      <div v-else-if="error" :class="$style.error">
        <p>{{ error }}</p>
        <button @click="fetchCommissions" :class="$style.retryButton">
          다시 시도
        </button>
      </div>

      <!-- 커미션 데이터 -->
      <div v-else>
        <!-- 검색 및 필터 -->
        <div :class="$style.controls">
          <!-- 카테고리 필터 -->
          <div :class="$style.typeFilter">
            <button :class="[
              $style.typeButton,
              { [$style.activeType]: selectedCategory === 'all' },
            ]" @click="selectCategory('all')">
              전체
            </button>
            <button v-for="opt in categoryOptions" :key="opt.value" :class="[
              $style.typeButton,
              { [$style.activeType]: selectedCategory === opt.value },
            ]" @click="selectCategory(opt.value)">
              {{ opt.label }}
            </button>
            <button v-if="userStore.isLogin" :class="[$style.registerButton, $style.desktopOnly]"
              @click="openCommissionForm">
              <img :class="$style.plusIcon" src="@/assets/images/plus.svg" alt="추가" />
              커미션 등록
            </button>
          </div>

          <!-- 정렬 버튼 -->
          <div :class="$style.sortFilter">
            <div :class="$style.searchBox">
              <input v-model="searchQuery" type="text" placeholder="커미션명 검색" :class="$style.searchInput" />
            </div>

            <div :class="$style.filterBox">
              <button @click="toggleSort" :class="[$style.sortButton, $style.toggleSortButton]">
                {{ sortLabel }}
                <img :class="$style.sortIcon" src="@/assets/images/swap_vert.png" alt="정렬 변경" />
              </button>
            </div>
          </div>
        </div>

        <!-- 커미션 목록 -->
        <div :class="$style.commissionList">
          <div v-if="paginatedCommissions.length === 0" :class="$style.emptyContainer">
            <p>등록된 커미션이 없습니다.</p>
          </div>
          <div v-else :class="$style.commissionGrid">
            <CommissionCard v-for="commission in paginatedCommissions" :key="commission.id" :commission="commission"
              :is-liked="isCommissionLiked(commission.id)" :show-like-button="true" :disabled="likeLoading"
              @like-toggle="toggleCommissionLike" />
          </div>
        </div>

        <!-- 페이지네이션 -->
        <div v-if="totalPages > 1" :class="$style.pagination">
          <button :class="$style.pageButton" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
            ‹
          </button>

          <button v-for="page in pageRange" :key="page" :class="[
            $style.pageButton,
            { [$style.activePage]: page === currentPage },
          ]" @click="goToPage(page)">
            {{ page }}
          </button>

          <button :class="$style.pageButton" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
            ›
          </button>
        </div>
      </div>

      <!-- 모바일 플로팅 등록 버튼 -->
      <button v-if="userStore.isLogin" :class="[$style.floatingButton, $style.mobileOnly]" @click="openCommissionForm">
        <span :class="$style.floatingText">+ 커미션</span>
      </button>

      <!-- 커미션 등록/수정 모달 -->
      <CommissionForm :is-visible="showCommissionForm" :mode="commissionFormMode"
        :commission-data="selectedCommissionData" @close-popup="closeCommissionForm" />
    </div>
  </div>
</template>

<script>
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import { toggleCommissionLike as toggleCommissionLikeApi } from "@/utils/write/user";
import CommissionForm from "@/components/modal/form/CommissionForm.vue";
import CommissionCard from "@/components/CommissionCard.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import LinkookLiveBanner from "@/components/layout/LinkookLiveBanner.vue";

export default {
  name: "CommissionList",
  components: {
    CommissionForm,
    CommissionCard,
    LoadingScreen,
    LinkookLiveBanner,
  },
  data() {
    return {
      commissions: [],
      loading: true,
      error: null,
      selectedCategory: "all",
      sortOption: "latest",
      categoryOptions: [
        { label: "믹싱", value: "mixing" },
        { label: "일러스트", value: "illustration" },
        { label: "디자인", value: "design" },
        { label: "영상", value: "video" },
      ],
      searchQuery: "",
      showCommissionForm: false,
      commissionFormMode: "create",
      selectedCommissionData: null,
      currentPage: 1,
      itemsPerPage: 10,
      userCommissionLikes: [], // 사용자가 찜한 커미션 목록
      likeLoading: false,
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },
    sortLabel() {
      const map = { latest: "최신순", oldest: "오래된순", name: "이름순" };
      return map[this.sortOption] || "최신순";
    },
    filteredCommissions() {
      let list = this.commissions.slice();

      // 카테고리 필터
      if (this.selectedCategory !== "all") {
        list = list.filter((c) => c.category === this.selectedCategory);
      }

      // 검색 필터
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter((c) => (c.name || "").toLowerCase().includes(q));
      }

      // 정렬
      if (this.sortOption === "latest") {
        list.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
      } else if (this.sortOption === "oldest") {
        list.sort((a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0));
      } else if (this.sortOption === "name") {
        list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ko"));
      }

      return list;
    },
    paginatedCommissions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCommissions.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredCommissions.length / this.itemsPerPage);
    },
    pageRange() {
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 5) {
        const pages = [];
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }

      let start, end;

      if (current <= 3) {
        start = 1;
        end = 5;
      } else if (current >= total - 2) {
        start = total - 4;
        end = total;
      } else {
        start = current - 2;
        end = current + 2;
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    sortOption() {
      this.currentPage = 1;
    },
    selectedCategory() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.loadUserLikes();
    await this.fetchCommissions();
  },
  methods: {
    async loadUserLikes() {
      if (!this.userStore.isLogin) {
        return;
      }

      try {
        const { getUserLikes } = await import("@/utils/read/user");
        const userLikes = await getUserLikes(this.userStore.email);
        this.userCommissionLikes = userLikes.commissionLikes || [];
      } catch (error) {
        console.error("사용자 찜 데이터 로드 오류:", error);
        this.userCommissionLikes = [];
      }
    },
    isCommissionLiked(commissionId) {
      return this.userCommissionLikes.includes(commissionId);
    },
    async toggleCommissionLike(commissionId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('likeFilter');
        return;
      }

      try {
        this.likeLoading = true;
        const isCurrentlyLiked = this.isCommissionLiked(commissionId);

        const newLikedState = await toggleCommissionLikeApi(
          commissionId,
          this.userStore.email,
          isCurrentlyLiked
        );

        if (newLikedState) {
          this.userCommissionLikes.push(commissionId);
        } else {
          this.userCommissionLikes = this.userCommissionLikes.filter(id => id !== commissionId);
        }
      } catch (error) {
        console.error("커미션 찜 오류:", error);
        this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
      } finally {
        this.likeLoading = false;
      }
    },
    async fetchCommissions() {
      try {
        this.loading = true;
        this.error = null;

        const commissionsQuery = query(
          collection(firestore, "commissions"),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(commissionsQuery);

        this.commissions = await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            let thumbnailUrl = data.thumbnail || "";

            // 썸네일 URL 처리
            if (thumbnailUrl && !thumbnailUrl.startsWith("http")) {
              try {
                const imageRef = ref(storage, `commission/${thumbnailUrl}`);
                thumbnailUrl = await getDownloadURL(imageRef);
              } catch (error) {
                console.error("커미션 썸네일 로드 실패:", error);
                thumbnailUrl = "";
              }
            }

            return {
              id: docSnap.id,
              category: data.category || "",
              name: data.name || "",
              description: data.description || "",
              link: data.link || "",
              thumbnailUrl: thumbnailUrl,
              createdAt: data.createdAt?.toDate() || new Date(),
            };
          })
        );
      } catch (error) {
        console.error("커미션 목록 로드 오류:", error);
        this.error = "커미션 목록을 불러오는 중 오류가 발생했습니다.";
        this.commissions = [];
      } finally {
        this.loading = false;
      }
    },
    openCommissionForm() {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        return;
      }
      this.commissionFormMode = "create";
      this.selectedCommissionData = null;
      this.showCommissionForm = true;
    },
    closeCommissionForm() {
      this.showCommissionForm = false;
      this.selectedCommissionData = null;
      this.fetchCommissions();
    },
    toggleSort() {
      const sortOptions = ["latest", "oldest", "name"];
      const currentIndex = sortOptions.indexOf(this.sortOption);
      const nextIndex = (currentIndex + 1) % sortOptions.length;
      this.sortOption = sortOptions[nextIndex];
    },
    selectCategory(cat) {
      this.selectedCategory = cat;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style module>
.container {
  width: 90%;
  margin: 0 auto;
  padding: 0px 20px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.error {
  text-align: center;
  color: #dc3545;
  margin: 20px 0;
}

.retryButton {
  background: #66bdff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}

.retryButton:hover {
  background: #4a9eff;
}

.controls {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
  overflow: hidden;
}

.searchBox {
  flex: 1;
  min-width: 100px;
}

.searchInput {
  width: 100%;
  height: 35px !important;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.searchInput:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

/* 타입 필터 스타일 */
.typeFilter {
  display: flex;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  white-space: nowrap;
  flex-shrink: 0;
  scrollbar-width: none;
  max-width: 100%;
  width: 100%;
  -ms-overflow-style: none;
  margin-top: 10px;
}

.typeFilter::-webkit-scrollbar {
  display: none;
}

.typeButton {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
}

.typeButton.activeType {
  background: #66bdff;
  color: white;
  border-color: #66bdff;
}

.registerButton {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundB", sans-serif;
}

.registerButton:hover {
  background: #4a9eff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.plusIcon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

/* 데스크톱/모바일 표시 제어 */
.desktopOnly {
  display: flex;
}

.mobileOnly {
  display: none;
}

.sortButton {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.2s ease;
}

.sortButton:hover {
  background: #66bdff;
  color: white;
  border-color: #66bdff;
}

.toggleSortButton {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  background: #66bdff;
  color: white;
  border-color: #66bdff;
  font-weight: 500;
  justify-content: space-between;
}

.sortFilter {
  width: 100%;
  display: flex;
  gap: 8px;
}

.sortIcon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  filter: brightness(0) invert(1);
}

.toggleSortButton:hover .sortIcon {
  transform: scale(1.2) rotate(180deg);
}

.filterBox {
  min-width: 80px;
}

.commissionList {
  width: 100%;
}

.emptyContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #666;
  font-size: 16px;
}

.commissionGrid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 90px;
}

/* 페이지네이션 스타일 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 90px;
}

.pageButton {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #ffffff;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 36px;
  text-align: center;
}

.pageButton:hover:not(.activePage):not(:disabled) {
  background: #f8f9fa;
  border-color: #ccc;
  color: #333;
}

.pageButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: #ccc;
  background: #f8f9fa;
}

.activePage {
  background: #222;
  color: #ffffff;
  border-color: #222;
}

/* 모바일 플로팅 버튼 */
.floatingButton {
  display: none;
}

.floatingButton:active {
  transform: translateY(0);
}

/* ========================================
   Medium (481px ~ 1024px)
   ======================================== */
@media (max-width: 1024px) {
  .container {
    width: 95%;
    padding: 0 15px;
  }

  .controls {
    gap: 15px;
    margin-bottom: 15px;
  }

  .typeFilter {
    gap: 6px;
    margin-top: 8px;
  }

  .typeButton {
    padding: 7px 14px;
    font-size: 11px;
    border-radius: 8px;
  }

  .registerButton {
    padding: 7px 14px;
    font-size: 13px;
  }

  .plusIcon {
    width: 14px;
    height: 14px;
  }

  .sortFilter {
    gap: 6px;
  }

  .searchInput {
    height: 32px !important;
    padding: 8px;
    font-size: 13px;
    border-radius: 6px;
  }

  .sortButton {
    padding: 7px 14px;
    font-size: 11px;
    border-radius: 8px;
  }

  .toggleSortButton {
    padding: 8px 14px;
  }

  .sortIcon {
    width: 14px;
    height: 14px;
  }

  .commissionGrid {
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 80px;
  }

  .emptyContainer {
    padding: 50px 15px;
    font-size: 15px;
  }

  .pagination {
    margin-top: 25px;
    gap: 6px;
    margin-bottom: 80px;
  }

  .pageButton {
    padding: 7px 10px;
    font-size: 13px;
    min-width: 32px;
  }

  .error {
    margin: 15px 0;
  }

  .retryButton {
    padding: 8px 16px;
    font-size: 13px;
  }
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px;
  }

  .controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-bottom: 12px;
    overflow: visible;
  }

  .typeFilter {
    gap: 5px;
    margin-top: 0;
    justify-content: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .typeButton {
    padding: 6px 10px;
    font-size: 10px;
    border-radius: 6px;
  }

  /* 모바일에서 데스크톱 버튼 숨기고 플로팅 버튼 표시 */
  .desktopOnly {
    display: none;
  }

  .mobileOnly {
    display: flex;
  }

  .sortFilter {
    gap: 6px;
  }

  .searchBox {
    min-width: 80px;
  }

  .searchInput {
    height: 30px !important;
    padding: 6px 8px;
    font-size: 12px;
    border-radius: 6px;
  }

  .filterBox {
    min-width: 70px;
  }

  .sortButton {
    padding: 6px 10px;
    font-size: 10px;
    border-radius: 6px;
  }

  .toggleSortButton {
    padding: 7px 10px;
    gap: 3px;
  }

  .sortIcon {
    width: 12px;
    height: 12px;
  }

  .commissionGrid {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 70px;
  }

  .emptyContainer {
    padding: 40px 10px;
    font-size: 14px;
  }

  .floatingButton {
    position: fixed;
    width: auto;
    background: #66bdff;
    border: none;
    border-radius: 20px;
    box-shadow: 0 4px 16px rgba(102, 189, 255, 0.4);
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    bottom: 70px;
    right: calc(50% - 500px);
    max-width: 1024px;
    margin-right: 12px;
    min-width: 90px;
    height: 40px;
    padding: 0 12px;
  }

  @media screen and (max-width: 1024px) {
    .floatingButton {
      right: 12px;
    }
  }

  .floatingButton:hover {
    background: #4a9eff;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 189, 255, 0.6);
  }

  .pagination {
    gap: 4px;
    margin-top: 20px;
    margin-bottom: 70px;
  }

  .pageButton {
    padding: 5px 8px;
    font-size: 11px;
    min-width: 26px;
    border-radius: 4px;
  }

  .error {
    margin: 12px 0;
    font-size: 14px;
  }

  .retryButton {
    padding: 8px 14px;
    font-size: 12px;
    border-radius: 4px;
  }
}
</style>
